/**
 * ExperienceManagement — Admin Experience Management Page
 *
 * Admin page for managing and moderating platform experiences.
 * Provides list view with filters, stats header, detail modal with moderation actions.
 *
 * Story: SCRUM-843 (FIX-H06: Admin experience management + approval workflow)
 *
 * AC-08: Route + nav entry
 * AC-09: Table with columns, filters, search, pagination
 * AC-10: Stats header with counts by moderation_status
 * AC-11: Detail modal with full info + moderation history
 * AC-12–16: Approve, reject, flag, suspend, unflag actions
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/shared/Modal';
import RejectReasonModal from '../../components/admin/RejectReasonModal';
import useAdminExperiences from '../../hooks/admin/useAdminExperiences';
import useAdminExperienceDetail from '../../hooks/admin/useAdminExperienceDetail';
import {
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Ban,
  Shield,
  Star,
  Clock,
  Compass,
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────

const MODERATION_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'suspended', label: 'Suspended' },
];

const STATUS_BADGES = {
  pending_review: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  flagged: { label: 'Flagged', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-800', icon: Ban },
};

// ── Formatting helpers ────────────────────────────────────────────────

const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) { return 'N/A'; }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

const formatDate = (dateStr) => {
  if (!dateStr) { return 'N/A'; }
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) { return 'N/A'; }
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// ── Component ─────────────────────────────────────────────────────────

export default function ExperienceManagement() {
  const navigate = useNavigate();

  // Toast state
  const [toast, setToast] = useState(null);

  // Reason modal state
  const [reasonModal, setReasonModal] = useState({ open: false, title: '', actionLabel: '', experienceId: null, actionType: null });

  const {
    experiences,
    pagination,
    stats,
    loading,
    error,
    actionLoading,
    filters,
    setFilters,
    refresh,
    approveExperience,
    rejectExperience,
    flagExperience,
    suspendExperience,
    unflagExperience,
  } = useAdminExperiences();

  const {
    experience: selectedExperience,
    loading: detailLoading,
    fetchDetail,
    clearDetail,
  } = useAdminExperienceDetail();

  // ── Toast helper ────────────────────────────────────────────────────

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Action handlers ─────────────────────────────────────────────────

  const handleApprove = useCallback(async (experienceId) => {
    try {
      await approveExperience(experienceId);
      showToast('Experience approved successfully');
      clearDetail();
    } catch (err) {
      showToast(err.message || 'Failed to approve experience', 'error');
    }
  }, [approveExperience, showToast, clearDetail]);

  const openReasonModal = useCallback((experienceId, actionType) => {
    const configs = {
      reject: { title: 'Reject Experience', actionLabel: 'Reject' },
      flag: { title: 'Flag Experience', actionLabel: 'Flag' },
      suspend: { title: 'Suspend Experience', actionLabel: 'Suspend' },
    };
    const config = configs[actionType];
    setReasonModal({ open: true, ...config, experienceId, actionType });
  }, []);

  const handleReasonSubmit = useCallback(async (reason) => {
    const { experienceId, actionType } = reasonModal;
    const actionMap = {
      reject: rejectExperience,
      flag: flagExperience,
      suspend: suspendExperience,
    };
    const actionFn = actionMap[actionType];
    if (!actionFn) { return; }

    await actionFn(experienceId, reason);
    showToast(`Experience ${actionType}ed successfully`);
    setReasonModal({ open: false, title: '', actionLabel: '', experienceId: null, actionType: null });
    clearDetail();
  }, [reasonModal, rejectExperience, flagExperience, suspendExperience, showToast, clearDetail]);

  const handleUnflag = useCallback(async (experienceId) => {
    try {
      await unflagExperience(experienceId);
      showToast('Flag cleared — experience approved');
      clearDetail();
    } catch (err) {
      showToast(err.message || 'Failed to clear flag', 'error');
    }
  }, [unflagExperience, showToast, clearDetail]);

  // ── Filter handlers ─────────────────────────────────────────────────

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  // ── Render helpers ──────────────────────────────────────────────────

  const renderStatusBadge = (status) => {
    const config = STATUS_BADGES[status] || STATUS_BADGES.pending_review;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const renderActiveBadge = (active) => {
    if (active) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">Active</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Inactive</span>;
  };

  const renderRating = (rating, count) => {
    if (!count) { return <span className="text-gray-400 text-xs">No reviews</span>; }
    return (
      <span className="inline-flex items-center gap-1 text-xs">
        <Star size={12} className="text-yellow-500 fill-yellow-500" />
        {rating.toFixed(1)} ({count})
      </span>
    );
  };

  // ── Action buttons for detail modal ─────────────────────────────────

  const renderActionButtons = (exp) => {
    if (!exp) { return null; }
    const status = exp.moderationStatus;
    const buttons = [];

    // Approve: visible for pending_review or flagged
    if (status === 'pending_review' || status === 'flagged') {
      buttons.push(
        <button
          key="approve"
          onClick={() => handleApprove(exp.id)}
          disabled={actionLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
        >
          <CheckCircle size={14} />
          Approve
        </button>
      );
    }

    // Reject: visible for pending_review
    if (status === 'pending_review') {
      buttons.push(
        <button
          key="reject"
          onClick={() => openReasonModal(exp.id, 'reject')}
          disabled={actionLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
        >
          <XCircle size={14} />
          Reject
        </button>
      );
    }

    // Flag: visible for approved
    if (status === 'approved') {
      buttons.push(
        <button
          key="flag"
          onClick={() => openReasonModal(exp.id, 'flag')}
          disabled={actionLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm font-medium"
        >
          <AlertTriangle size={14} />
          Flag
        </button>
      );
    }

    // Suspend: visible for approved or flagged
    if (status === 'approved' || status === 'flagged') {
      buttons.push(
        <button
          key="suspend"
          onClick={() => openReasonModal(exp.id, 'suspend')}
          disabled={actionLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 text-sm font-medium"
        >
          <Ban size={14} />
          Suspend
        </button>
      );
    }

    // Clear Flag: visible for flagged
    if (status === 'flagged') {
      buttons.push(
        <button
          key="unflag"
          onClick={() => handleUnflag(exp.id)}
          disabled={actionLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
        >
          <Shield size={14} />
          Clear Flag
        </button>
      );
    }

    if (buttons.length === 0) { return null; }
    return <div className="flex flex-wrap gap-2">{buttons}</div>;
  };

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="experiences"
      onNavigate={(id, path) => path && navigate(path)}
    >
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Experience Management</h1>
          <button
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 transition text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* TOAST */}
        {toast && (
          <div
            className={`p-3 rounded-lg text-sm font-medium ${
              toast.type === 'error'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}
            role="alert"
          >
            {toast.message}
          </div>
        )}

        {/* STATS CARDS (AC-10) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Pending Review</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pendingReview || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Approved</p>
            <p className="text-2xl font-bold text-green-600">{stats.approved || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Flagged</p>
            <p className="text-2xl font-bold text-orange-600">{stats.flagged || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Suspended</p>
            <p className="text-2xl font-bold text-red-600">{stats.suspended || 0}</p>
          </div>
        </div>

        {/* SEARCH + FILTERS (AC-09) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search by title, guide name, email..."
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              />
            </form>
            <select
              value={filters.moderation_status}
              onChange={(e) => setFilters(prev => ({ ...prev, moderation_status: e.target.value, page: 1 }))}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              {MODERATION_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* TABLE (AC-09) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
              <span className="ml-3 text-gray-600">Loading experiences...</span>
            </div>
          ) : experiences.length === 0 ? (
            <div className="text-center py-12">
              <Compass className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No experiences found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Title</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Guide</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Category</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden lg:table-cell">Price</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden lg:table-cell">Rating</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Active</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Created</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {experiences.map((exp) => (
                    <tr key={exp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {exp.coverPhotoUrl && (
                            <img
                              src={exp.coverPhotoUrl}
                              alt=""
                              className="w-8 h-8 rounded object-cover flex-shrink-0"
                            />
                          )}
                          <span className="text-gray-900 font-medium truncate max-w-[200px]">{exp.title}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-gray-900">{exp.guideName || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-gray-600">{exp.categoryName || '—'}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="font-medium text-green-600">{formatCurrency(exp.basePriceCents)}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {renderRating(exp.averageRating, exp.totalReviews)}
                      </td>
                      <td className="px-4 py-3">{renderStatusBadge(exp.moderationStatus)}</td>
                      <td className="px-4 py-3 hidden sm:table-cell">{renderActiveBadge(exp.active)}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-gray-500 text-xs">
                        {formatDate(exp.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => fetchDetail(exp.id)}
                          className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-dark text-xs font-medium"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION */}
          {pagination.totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={filters.page <= 1}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                  disabled={filters.page >= pagination.totalPages}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* DETAIL MODAL (AC-11) */}
        <Modal
          isOpen={!!selectedExperience}
          onClose={clearDetail}
          title="Experience Details"
          footer={
            <div className="flex items-center justify-between w-full">
              <div>{selectedExperience && renderActionButtons(selectedExperience)}</div>
              <button
                onClick={clearDetail}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Close
              </button>
            </div>
          }
        >
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
          ) : selectedExperience ? (
            <div className="space-y-6">
              {/* Experience Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Experience Info</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="col-span-2"><span className="text-gray-500">Title:</span> <span className="font-medium">{selectedExperience.title}</span></div>
                  <div><span className="text-gray-500">Status:</span> {renderStatusBadge(selectedExperience.moderationStatus)}</div>
                  <div><span className="text-gray-500">Active:</span> {renderActiveBadge(selectedExperience.active)}</div>
                  <div><span className="text-gray-500">Price:</span> {formatCurrency(selectedExperience.basePriceCents)}</div>
                  <div><span className="text-gray-500">Difficulty:</span> {selectedExperience.difficultyLevel || '—'}</div>
                  <div><span className="text-gray-500">Duration:</span> {selectedExperience.durationHours}h{selectedExperience.durationDays > 1 ? ` / ${selectedExperience.durationDays}d` : ''}</div>
                  <div><span className="text-gray-500">Participants:</span> {selectedExperience.minParticipants}–{selectedExperience.maxParticipants}</div>
                  {selectedExperience.meetingLocationName && (
                    <div><span className="text-gray-500">Location:</span> {selectedExperience.meetingLocationName}{selectedExperience.meetingState ? `, ${selectedExperience.meetingState}` : ''}</div>
                  )}
                  {selectedExperience.minimumAge && (
                    <div><span className="text-gray-500">Min Age:</span> {selectedExperience.minimumAge}</div>
                  )}
                  <div><span className="text-gray-500">Category:</span> {selectedExperience.categoryName || '—'}</div>
                  <div><span className="text-gray-500">Created:</span> {formatDate(selectedExperience.createdAt)}</div>
                </div>
                {selectedExperience.description && (
                  <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-2">{selectedExperience.description}</p>
                )}
              </div>

              {/* Guide */}
              {selectedExperience.guide && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Guide</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">Name:</span> {selectedExperience.guide.name}</div>
                    <div><span className="text-gray-500">Email:</span> {selectedExperience.guide.email}</div>
                    {selectedExperience.guide.businessName && (
                      <div><span className="text-gray-500">Business:</span> {selectedExperience.guide.businessName}</div>
                    )}
                    <div><span className="text-gray-500">Approval:</span> {selectedExperience.guide.approvalStatus}</div>
                    <div><span className="text-gray-500">Experience Count:</span> {selectedExperience.guide.experienceCount}</div>
                  </div>
                </div>
              )}

              {/* Review Stats */}
              {selectedExperience.reviewStats && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Reviews</h3>
                  <div className="flex items-center gap-4 text-sm">
                    {renderRating(selectedExperience.reviewStats.averageRating, selectedExperience.reviewStats.totalReviews)}
                    <span className="text-gray-500">{selectedExperience.reviewStats.totalReviews} reviews</span>
                  </div>
                </div>
              )}

              {/* Photos */}
              {selectedExperience.photos && selectedExperience.photos.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Photos</h3>
                  <div className="flex gap-2 overflow-x-auto">
                    {selectedExperience.photos.map((photo) => (
                      <img
                        key={photo.id}
                        src={photo.url}
                        alt={photo.altText || photo.caption || 'Experience photo'}
                        className="w-20 h-20 rounded object-cover flex-shrink-0"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Moderation Info */}
              {(selectedExperience.moderationNotes || selectedExperience.flaggedReason) && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Moderation Notes</h3>
                  {selectedExperience.moderationNotes && (
                    <p className="text-sm text-gray-600 mb-1"><span className="text-gray-500">Notes:</span> {selectedExperience.moderationNotes}</p>
                  )}
                  {selectedExperience.flaggedReason && (
                    <p className="text-sm text-orange-700"><span className="text-gray-500">Flag Reason:</span> {selectedExperience.flaggedReason}</p>
                  )}
                  {selectedExperience.flaggedAt && (
                    <p className="text-xs text-gray-400">Flagged: {formatDateTime(selectedExperience.flaggedAt)}</p>
                  )}
                </div>
              )}

              {/* Moderation History */}
              {selectedExperience.moderationLog && selectedExperience.moderationLog.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Moderation History</h3>
                  <div className="space-y-2">
                    {selectedExperience.moderationLog.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 text-xs bg-gray-50 rounded p-2">
                        <div className="flex-1">
                          <span className="font-medium capitalize">{log.action}</span>
                          <span className="text-gray-400 mx-1">&mdash;</span>
                          <span className="text-gray-500">{log.previousStatus} → {log.newStatus}</span>
                          {log.reason && <p className="text-gray-600 mt-0.5">Reason: {log.reason}</p>}
                        </div>
                        <div className="text-right text-gray-400 flex-shrink-0">
                          <div>{log.moderatorName}</div>
                          <div>{formatDateTime(log.createdAt)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </Modal>

        {/* REASON MODAL (AC-13, AC-14, AC-15) */}
        <RejectReasonModal
          isOpen={reasonModal.open}
          onClose={() => setReasonModal({ open: false, title: '', actionLabel: '', experienceId: null, actionType: null })}
          onConfirm={handleReasonSubmit}
          title={reasonModal.title}
          actionLabel={reasonModal.actionLabel}
          loading={actionLoading}
        />
      </div>
    </DashboardLayout>
  );
}
